import { request } from "../../utils/config"

export interface SubscriptionSettings {
  id: number
  enabled: boolean
  periodPrice: number
  periodDays: number
  currency: string
  remindDaysBefore: number
  graceDays: number
  /** Yangi korxonaga beriladigan sinov kunlari */
  trialDays: number
  /** Korxonalar o'zi ro'yxatdan o'ta oladimi */
  allowSelfRegistration: boolean
  /** Ro'yxatdan o'tish uchun taklif kaliti talab qilinadimi */
  registrationRequiresKey: boolean
  /** Bir kunda ochilishi mumkin bo'lgan korxonalar soni. 0 — cheksiz. */
  maxNewTenantsPerDay: number
}

export interface SubscriptionPayment {
  id: number
  amount: number
  currency: string
  daysAdded: number
  paidUntilAfter: string | null
  source: string
  note: string | null
  inserted: string
  createdByName: string | null
}

export interface SubscriptionStatus {
  userId: number
  userName: string
  email: string
  roleId: number
  roleName: string | null
  paidUntil: string | null
  daysLeft: number | null
  blocked: boolean
  blockReason: string | null
  applicable: boolean
  showWarning: boolean
  periodPrice: number
  periodDays: number
  currency: string
  dependentUserCount: number
  payments: SubscriptionPayment[]
}

const auth = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
})

/** Joriy foydalanuvchining o'z holati — bloklangan bo'lsa ham ochiq */
export const GET_MY_SUBSCRIPTION = async (): Promise<SubscriptionStatus> => {
  const res = await request({ url: `Subscription/My`, headers: auth(), silent: true })
  return res.data
}

export const GET_SUBSCRIPTION_SETTINGS = async (): Promise<SubscriptionSettings> => {
  const res = await request({ url: `Subscription/Settings`, headers: auth() })
  return res.data
}

export const SAVE_SUBSCRIPTION_SETTINGS = async (
  input: Partial<SubscriptionSettings>
): Promise<SubscriptionSettings> => {
  const res = await request({
    url: `Subscription/Settings`,
    method: "PUT",
    data: input,
    headers: auth(),
  })
  return res.data
}

export const GET_SUBSCRIPTION_USERS = async (): Promise<SubscriptionStatus[]> => {
  const res = await request({ url: `Subscription/Users`, headers: auth() })
  return res.data || []
}

export const GET_SUBSCRIPTION_USER = async (userId: number): Promise<SubscriptionStatus> => {
  const res = await request({ url: `Subscription/Users/${userId}`, headers: auth() })
  return res.data
}

export const ADD_SUBSCRIPTION_PAYMENT = async (input: {
  userId: number
  amount: number
  days?: number
  note?: string
}): Promise<SubscriptionStatus> => {
  const res = await request({
    url: `Subscription/Payments`,
    method: "POST",
    data: input,
    headers: auth(),
  })
  return res.data
}

export const BLOCK_SUBSCRIPTION_USER = async (input: {
  userId: number
  reason?: string
}) => {
  const res = await request({
    url: `Subscription/Users/${input.userId}/Block`,
    method: "POST",
    data: { reason: input.reason },
    headers: auth(),
  })
  return res.data
}

export const UNBLOCK_SUBSCRIPTION_USER = async (userId: number) => {
  const res = await request({
    url: `Subscription/Users/${userId}/Unblock`,
    method: "POST",
    headers: auth(),
  })
  return res.data
}

// ---------------- Aktivatsiya kalitlari ----------------

/** Kalit tarixidagi bitta voqea */
export interface ActivationKeyEvent {
  /** created / used / revoked */
  action: string
  at: string
  byName: string | null
  tenantName: string | null
  detail: string | null
}

export interface ActivationKey {
  id: number
  code: string
  days: number
  amount: number
  /** Kalit shu KORXONA uchun. Bo'sh — istalgan korxona ishlata oladi. */
  targetTenantId: number | null
  targetTenantName: string | null
  /** Eski usul — muayyan foydalanuvchiga atalgan kalit */
  targetUserId: number | null
  targetUserName: string | null
  expiresAt: string | null
  used: boolean
  usedByUserId: number | null
  usedByUserName: string | null
  usedByTenantId: number | null
  usedByTenantName: string | null
  forRegistration: boolean
  revoked: boolean
  revokedAt: string | null
  revokedByName: string | null
  /** Kalitning butun tarixi: yaratilgan, ishlatilgan, bekor qilingan */
  history: ActivationKeyEvent[]
  usedAt: string | null
  note: string | null
  inserted: string
  createdByName: string | null
}

export const GET_ACTIVATION_KEYS = async (): Promise<ActivationKey[]> => {
  const res = await request({ url: `Subscription/Keys`, headers: auth() })
  return res.data || []
}

export const CREATE_ACTIVATION_KEYS = async (input: {
  count: number
  days: number
  amount: number
  /** Kalitni shu korxonaga biriktirish. Bo'sh — istalgan korxonaga. */
  targetTenantId?: number | null
  /** Yangi korxona ochish uchun taklif kaliti */
  forRegistration?: boolean
  note?: string
}): Promise<ActivationKey[]> => {
  const res = await request({
    url: `Subscription/Keys`,
    method: "POST",
    data: input,
    headers: auth(),
  })
  return res.data || []
}

/**
 * Kalitni bekor qiladi. Ishlatilgan bo'lsa bergan kunlari korxonadan
 * qaytarib olinadi — o'chirishdan farqi shunda.
 */
export const REVOKE_ACTIVATION_KEY = async (id: number) => {
  const res = await request({
    url: `Subscription/Keys/${id}/Revoke`,
    method: "POST",
    headers: auth(),
  })
  return res.data
}

export const DELETE_ACTIVATION_KEY = async (id: number) => {
  const res = await request({
    url: `Subscription/Keys/${id}`,
    method: "DELETE",
    headers: auth(),
  })
  return res.data
}

/** Mijoz kalitni kiritadi — bloklangan holatda ham ishlaydi */
export const REDEEM_ACTIVATION_KEY = async (code: string): Promise<SubscriptionStatus> => {
  const res = await request({
    url: `Subscription/Redeem`,
    method: "POST",
    data: { code },
    headers: auth(),
  })
  return res.data
}

export const RUN_SUBSCRIPTION_CHECK = async () => {
  const res = await request({
    url: `Subscription/RunCheck`,
    method: "POST",
    headers: auth(),
  })
  return res.data
}
